import { Component, ElementRef, HostListener, OnDestroy, OnInit, computed, effect, inject, signal, viewChild } from '@angular/core';
import { ChatContact, ChatMessage, buildThreadEntries, chatAvatarColor, chatInitials, chatName, formatChatDate } from '@core/models/chat.model';
import { ChatService } from '@services/chat.service';

@Component({
  selector: 'app-chat-widget',
  templateUrl: './chat-widget.html'
})
export class ChatWidget implements OnInit, OnDestroy {
  protected readonly chat = inject(ChatService);
  private readonly messagesEl = viewChild<ElementRef<HTMLElement>>('messagesEl');
  protected readonly draft = signal('');

  protected readonly selectedIds = signal<ReadonlySet<string>>(new Set());
  protected readonly confirming = signal(false);
  protected readonly deleting = signal(false);
  protected readonly threadEntries = computed(() => buildThreadEntries(this.chat.messages()));
  protected readonly selectedCount = computed(() => this.selectedIds().size);
  protected readonly deletePrompt = computed(() =>
    this.selectedCount() === 1 ? 'Delete this message?' : `Delete ${this.selectedCount()} messages?`
  );
  protected readonly unreadLabel = computed(() => {
    const total = this.chat.unreadTotal();
    if (total <= 0) {
      return '';
    }
    return total > 99 ? '99+' : String(total);
  });

  private readonly scrollWithThread = effect(() => {
    const token = this.chat.bottomScrollToken();
    if (!token || this.chat.view() !== 'thread') {
      return;
    }
    queueMicrotask(() => this.scrollToBottom());
  });

  private readonly clearSelectionOnThreadChange = effect(() => {
    this.chat.activeContactId();
    this.chat.view();
    this.selectedIds.set(new Set());
    this.confirming.set(false);
  });

  private readonly dropMissingSelection = effect(() => {
    const live = new Set(
      this.chat.messages().filter((message) => this.chat.isMine(message)).map((message) => message.uniqueChatMessageId)
    );
    const selected = this.selectedIds();
    const next = [...selected].filter((id) => live.has(id));
    if (next.length !== selected.size) {
      this.selectedIds.set(new Set(next));
    }
  });

  ngOnInit(): void {
    this.chat.syncStudy();
  }

  ngOnDestroy(): void {
    this.chat.disconnect();
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    if (!this.chat.panelOpen()) {
      return;
    }
    if (this.confirming()) {
      this.cancelDelete();
      return;
    }
    if (this.selectedCount() > 0) {
      this.clearSelection();
      return;
    }
    if (this.chat.view() === 'thread') {
      this.chat.backToList();
      return;
    }
    this.chat.closePanel();
  }

  protected hidePhoto(contact: ChatContact): void {
    contact.photo = null;
  }

  protected name(firstName: string, lastName: string): string {
    return chatName(firstName, lastName);
  }

  protected pairLabel(firstFirstName: string, firstLastName: string, secondFirstName: string, secondLastName: string): string {
    return `${this.name(firstFirstName, firstLastName)} · ${this.name(secondFirstName, secondLastName)}`;
  }

  protected initials(firstName: string, lastName: string): string {
    return chatInitials(firstName, lastName);
  }

  protected avatarColor(firstName: string, lastName: string): string {
    return chatAvatarColor(this.name(firstName, lastName));
  }

  protected stamp(value: string | null): string {
    return formatChatDate(value);
  }

  protected messageTime(message: ChatMessage): string {
    return formatChatDate(message.sentDate);
  }

  protected onSearch(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.chat.search.set(value);
  }

  protected async openContact(uniqueUserId: string): Promise<void> {
    await this.chat.openThread(uniqueUserId);
  }

  protected async loadOlder(): Promise<void> {
    const el = this.messagesEl()?.nativeElement;
    const previousHeight = el?.scrollHeight ?? 0;
    const previousTop = el?.scrollTop ?? 0;
    await this.chat.loadOlder();
    requestAnimationFrame(() => {
      if (!el) {
        return;
      }
      el.scrollTop = el.scrollHeight - previousHeight + previousTop;
    });
  }

  protected onDraft(event: Event): void {
    this.draft.set((event.target as HTMLInputElement).value);
  }

  protected async send(event: Event): Promise<void> {
    event.preventDefault();
    const sent = await this.chat.send(this.draft());
    if (sent) {
      this.draft.set('');
    }
  }

  protected isSelected(message: ChatMessage): boolean {
    return this.selectedIds().has(message.uniqueChatMessageId);
  }

  protected onBubbleClick(message: ChatMessage): void {
    if (this.chat.isMine(message)) {
      this.toggleSelect(message);
    }
  }

  protected toggleSelect(message: ChatMessage): void {
    if (!this.chat.isMine(message) || this.deleting()) {
      return;
    }
    const next = new Set(this.selectedIds());
    if (next.has(message.uniqueChatMessageId)) {
      next.delete(message.uniqueChatMessageId);
    } else {
      next.add(message.uniqueChatMessageId);
    }
    this.selectedIds.set(next);
  }

  protected clearSelection(): void {
    this.confirming.set(false);
    this.selectedIds.set(new Set());
  }

  protected askDelete(): void {
    if (this.selectedCount() > 0 && !this.deleting()) {
      this.confirming.set(true);
    }
  }

  protected cancelDelete(): void {
    if (!this.deleting()) {
      this.confirming.set(false);
    }
  }

  protected async confirmDelete(): Promise<void> {
    const ids = [...this.selectedIds()];
    if (!ids.length || this.deleting()) {
      return;
    }
    this.deleting.set(true);
    try {
      await this.chat.deleteMessages(ids);
    } finally {
      this.deleting.set(false);
      this.clearSelection();
    }
  }

  private scrollToBottom(): void {
    const el = this.messagesEl()?.nativeElement;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }
}
